import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { useMemo, useState } from 'react'

import { ModalForm } from '@/components/Modal/ModalForm'
import { TextFormField } from '@/components/UberForm'
import CheckboxFormField from '@/components/UberForm/Helpers/CheckBoxFormField'
import { Validator } from '@/components/UberForm/Validators'
import { duplicateNodeRecursive } from '@/endpoints'
import { StructureDto, StructureObjectDto } from '@/endpoints/models'
import { useAppContext, useAppDispatch, useAppStore } from '@/hooks'
import { findSystemNodeId } from '@/store/modules/node/helpers'
import { apiCallAction, checkUniqueName } from '@/store/utils'
import { asyncDebounce } from '@/utils/async'
import { logError } from '@/utils/errorHandling'

import { useTreePanelOperations } from '../TreePanelContext'

type Props = {
	node?: StructureDto
	onClose: () => void
}

type FormData = {
	suffix: string
	useSuffixOnly4System?: boolean
}

export const DuplicateNodeModal = ({ node, onClose }: Props) => {
	const { t } = useAppContext()
	const nodes = useAppStore((state) => state.node.nodes)
	const dispatch = useAppDispatch()
	const [loading, setLoading] = useState(false)
	const { refreshSystemsSilent } = useTreePanelOperations()

	const systemNodeId = useMemo(
		() => node && findSystemNodeId(node, nodes),
		[node, nodes],
	)

	const handleSubmit = async (form: Partial<FormData>) => {
		setLoading(true)

		await dispatch(
			apiCallAction(() => duplicateNodeRecursive(node?.id as number, form)),
		)

		refreshSystemsSilent().catch((e) =>
			logError(e, { operation: 'DuplicateNodeModal.refreshSystemsSilent' }),
		)

		setLoading(false)
		onClose()
	}

	const suffixValidator = (): Validator =>
		asyncDebounce(async (value) => {
			if (!value || typeof value !== 'string') {
				return
			}

			const res = await dispatch(
				checkUniqueName(
					node?.name + value,
					node?.type == StructureObjectDto.TypeEnum.SYSTEM
						? undefined
						: systemNodeId,
				),
			)

			if (res.errors && (res.errors?.length ?? 0) > 0) {
				return res.errors.join('\n')
			}
		})

	return (
		<ModalForm<FormData>
			saveTitle={t('DUPLICATE')}
			saveIcon={faPlus}
			cancelTitle={t('CANCEL')}
			onSubmit={handleSubmit}
			open={true}
			isLoading={loading}
			onClose={onClose}
			contentStyle={{
				width: '400px',
			}}
			header={t('DUPLICATE') + ': ' + node?.name}
		>
			<TextFormField
				title={t('SUFFIX')}
				name="suffix"
				required
				autoFocus
				enableAutocomplete={false}
				validators={[suffixValidator()]}
			/>
			{node?.type == StructureObjectDto.TypeEnum.SYSTEM && (
				<CheckboxFormField
					title={t('USE_SUFFIX_ONLY_4_SYSTEM')}
					name="useSuffixOnly4System"
				/>
			)}
		</ModalForm>
	)
}
